import React, { useState } from "react";
import { DisplaySection } from "app/main/utils/uiUtils";
import { LinearProgress, Typography, Box, Collapse, IconButton } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";

const LoadStrategySummaryView = ({ loadToUse, strategy, variant }) => {
	const [isExpanded, setIsExpanded] = useState(false);

	if (!loadToUse || !strategy) return null;

	let progress_payment = 0;
	let progress_loaded_miles = 0;
	let progress_empty_miles = 0;
	let progress_duration = 0;
	let progress_rpm = 0;

	if (strategy.progress) {
		progress_payment = strategy.progress.total_payment;
		progress_loaded_miles = strategy.progress.total_loaded_miles;
		progress_empty_miles = strategy.progress.total_empty_miles;
		progress_duration = strategy.progress.total_duration;
		progress_rpm = strategy.progress.total_rpm;
	}

	const actualPrice = progress_payment + (loadToUse.price || loadToUse.est_price);
	const actualMiles = progress_loaded_miles + (loadToUse.miles || 0);
	const distanceToOrigin = progress_empty_miles + (loadToUse.distance_to_origin || 0);
	const totalMiles = actualMiles + distanceToOrigin;
	const actualRPM = actualPrice && actualMiles ? actualPrice / actualMiles : 0;
	const actualDuration = progress_duration + (loadToUse.duration || 0);

	const guarantee_payment = strategy.guarantee?.total_payment;
	const guarantee_loaded_miles = strategy.guarantee?.total_loaded_miles;
	const guarantee_empty_miles = strategy.guarantee?.total_empty_miles;
	const guarantee_duration = strategy.guarantee?.total_duration;
	const guarantee_rpm = strategy.guarantee?.total_rpm;

	const calculateProgress = (progress, guarantee) => {
		if (!guarantee || guarantee === 0) return 0;
		return (progress / guarantee) * 100;
	};

	const getProgressColor = (value, isInverse = false) => {
		if (isInverse) {
			return value <= 100 ? "success" : "error";
		}
		return value >= 100 ? "success" : "error";
	};

	const renderProgressBar = (
		label,
		value,
		guarantee,
		isInverse = false,
		format = (val) => `$${val?.toFixed(2) || 0}`
	) => {
		const progress = calculateProgress(value, guarantee);
		const color = getProgressColor(progress, isInverse);

		return (
			<Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
				<Box sx={{ minWidth: "200px" }}>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
						{label}
					</Typography>
					<Typography variant="h6" fontWeight="medium">
						{format(value)} / {format(guarantee)}
					</Typography>
				</Box>
				<Box sx={{ flex: 1, position: "relative" }}>
					<LinearProgress
						variant="determinate"
						value={progress}
						color={color}
						max={200}
						sx={{
							height: 12,
							borderRadius: 4,
							"& .MuiLinearProgress-bar": {
								borderRadius: 4,
							},
						}}
					/>
				</Box>
			</Box>
		);
	};

	return (
		<DisplaySection
			title={
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
					<Typography variant="subtitle">Projection</Typography>
					<IconButton size="small" onClick={() => setIsExpanded(!isExpanded)} sx={{ p: 0 }}>
						{isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
					</IconButton>
				</Box>
			}
			classes={{ root: "mx-0 mt-0" }}
		>
			<Collapse in={isExpanded}>
				<div className={`flex flex-col ${variant === "skinny" ? "px-2" : "px-4"} py-2`}>
					{renderProgressBar("Payment", actualPrice, guarantee_payment)}
					{renderProgressBar(
						"Total Miles",
						totalMiles,
						guarantee_loaded_miles + guarantee_empty_miles,
						true,
						(val) => `${val?.toFixed(0) || 0} mi`
					)}
					{renderProgressBar("RPM", actualRPM, guarantee_rpm)}
					{renderProgressBar(
						"Duration",
						actualDuration,
						guarantee_duration,
						true,
						(val) => `${val?.toFixed(0) || 0} days`
					)}
				</div>
			</Collapse>
		</DisplaySection>
	);
};

export default LoadStrategySummaryView;
